import React, { useState } from 'react';
import { GoalIcon, TipIcon } from '../../icons';

interface MissionVisionStepProps {
  initialMission?: string;
  initialVision?: string;
  onNext: (mission: string, vision: string) => void;
  onBack: () => void;
}

/**
 * MissionVisionStep - Formuleer de Missie en Visie voor dit doel
 * 
 * Komt na type confirmation en vóór commitment video
 * Dit geeft richting en betekenis aan het doel
 */
const MissionVisionStep: React.FC<MissionVisionStepProps> = ({
  initialMission = '',
  initialVision = '',
  onNext,
  onBack,
}) => {
  const [mission, setMission] = useState(initialMission);
  const [vision, setVision] = useState(initialVision);

  const canContinue = mission.trim().length > 0 && vision.trim().length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canContinue) {
      onNext(mission.trim(), vision.trim());
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-3"><GoalIcon size={32} /></div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Missie & Visie
          </h2>
          <p className="text-gray-600">
            Geef richting en betekenis aan je doel
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Missie - Het "Waarom" */}
          <div>
            <label className="block mb-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg font-medium text-gray-900">
                  Missie
                </span>
                <span className="text-sm text-gray-500">Het "waarom"</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Wat wil je achterlaten met dit doel? Wat is je bijdrage?
              </p>
              <textarea
                value={mission}
                onChange={(e) => setMission(e.target.value)}
                placeholder="Bijv: Ik wil andere ondernemers helpen om hun passie om te zetten in een duurzaam bedrijf, zodat ze meer vrijheid en impact kunnen creëren..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              />
            </label>
            <div className="flex justify-end">
              <span className={`text-xs ${mission.length > 20 ? 'text-gray-400' : 'text-orange-500'}`}>
                {mission.length > 0 ? `${mission.length} karakters` : 'Minimaal 20 karakters aanbevolen'}
              </span>
            </div>
          </div>

          {/* Visie - Het "Waarheen" */}
          <div>
            <label className="block mb-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg font-medium text-gray-900">
                  Visie
                </span>
                <span className="text-sm text-gray-500">Het "waarheen"</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Als dit doel volledig tot bloei is gekomen, hoe ziet dat eruit?
              </p>
              <textarea
                value={vision}
                onChange={(e) => setVision(e.target.value)}
                placeholder="Bijv: Ik zie mezelf als erkend expert in mijn vakgebied, met een community van betrokken ondernemers die elkaar ondersteunen. Mijn bedrijf draait soepel en geeft me de ruimte om te groeien..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              />
            </label>
            <div className="flex justify-end">
              <span className={`text-xs ${vision.length > 20 ? 'text-gray-400' : 'text-orange-500'}`}>
                {vision.length > 0 ? `${vision.length} karakters` : 'Minimaal 20 karakters aanbevolen'}
              </span>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <TipIcon size={20} className="text-blue-600 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Waarom is dit belangrijk?</p>
                <p className="text-blue-800">
                  Je missie en visie geven richting aan je doel en helpen je om gefocust te blijven. 
                  Ze herinneren je aan het grotere plaatje wanneer de uitdagingen komen.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              ← Terug
            </button>
            <button
              type="submit"
              disabled={!canContinue}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                canContinue
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Volgende stap →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MissionVisionStep;
